function linesFromValidationErrors(errors: unknown): string[] {
    if (!errors || typeof errors !== 'object') return [];
    return Object.entries(errors as Record<string, unknown>).flatMap(([field, value]) => {
        if (Array.isArray(value)) {
            return value.map((v) => `${field}: ${String(v)}`);
        }
        if (value != null && String(value).trim()) {
            return [`${field}: ${String(value)}`];
        }
        return [];
    });
}

function linesFromErrorDetails(details: unknown): string[] {
    if (!Array.isArray(details)) return [];
    return details
        .map((row) => {
            if (!row || typeof row !== 'object') return '';
            const r = row as Record<string, unknown>;
            const msg = r.message ?? r.Message ?? r.description ?? r.Description;
            const name = r.name ?? r.objectName;
            const text = msg != null ? String(msg).trim() : '';
            const label = name != null ? String(name).trim() : '';
            if (text && label) return `${label}: ${text}`;
            return text || label;
        })
        .filter(Boolean);
}

function linesFromGoodLeapArray(items: unknown): string[] {
    if (!Array.isArray(items)) return [];
    return items
        .map((item) => {
            if (!item || typeof item !== 'object') return '';
            const r = item as Record<string, unknown>;
            return String(r.description ?? r.message ?? r.Message ?? '').trim();
        })
        .filter(Boolean);
}

/**
 * Extract user-facing text from OneButton / finance partner API responses.
 * Bodies may be objects or JSON strings: `{"message":"...","requestId":"..."}`.
 */
export function parseContractApiMessageBody(body: unknown): string | null {
    if (body == null) return null;

    if (typeof body === 'string') {
        const trimmed = body.trim();
        if (!trimmed) return null;
        try {
            const parsed = JSON.parse(trimmed) as unknown;
            const nested = parseContractApiMessageBody(parsed);
            if (nested) return nested;
        } catch {
            return trimmed;
        }
        return trimmed;
    }

    if (Array.isArray(body)) {
        const fromArray = linesFromGoodLeapArray(body);
        if (fromArray.length) return fromArray.join('\n');
        return null;
    }

    if (typeof body === 'object') {
        const record = body as Record<string, unknown>;

        const errorDetails = record.ErrorDetails ?? record.errorDetails;
        const detailLines = linesFromErrorDetails(errorDetails);
        if (detailLines.length) return detailLines.join('\n');

        const validationLines = linesFromValidationErrors(record.errors);
        if (validationLines.length) return validationLines.join('\n');

        const nestedError = record.error;
        if (nestedError != null) {
            const fromNested = parseContractApiMessageBody(nestedError);
            if (fromNested) return fromNested;
        }

        const title = record.title ?? record.Title;
        if (title != null && String(title).trim()) return String(title).trim();

        const direct = record.message ?? record.Message;
        if (direct != null) {
            if (typeof direct === 'string') {
                const nested = parseContractApiMessageBody(direct);
                if (nested) return nested;
            } else if (String(direct).trim()) {
                return String(direct).trim();
            }
        }
    }

    return null;
}
/** Axios / fetch failure from contract APIs. */
export function getContractApiErrorMessage(error: unknown, fallback = 'An error occurred.'): string {
    const ax = error as { response?: { data?: unknown }; message?: string };
    const fromData = parseContractApiMessageBody(ax?.response?.data);
    if (fromData) return fromData;

    const msg = String(ax?.message ?? '').trim();
    if (msg && !/^Request failed with status code \d+$/i.test(msg)) {
        return msg;
    }

    return fallback;
}

/** Non-throwing API result (200 with error payload or `IsSuccessful: false`). */
export function getContractApiResultErrorMessage(res: unknown, fallback: string): string {
    const fromRes = parseContractApiMessageBody(res);
    if (fromRes && typeof fromRes == 'string') return fromRes;

    if (res && typeof res === 'object') {
        const record = res as Record<string, unknown>;
        const fromMessage = parseContractApiMessageBody(record.Message ?? record.message);
        if (fromMessage) return fromMessage;
    }

    return fallback;
}
